/**
 * Camera feel (plan G9, spec §8-§9): boost FOV kick, subtle speed FOV, screen shake on hits taken by the player
 * and a small boost rumble. Runs first among the features (FEATURE_ORDER.camera), right after the loop has run
 * the base rig (`updateCameraRig`) and copied `rig.rot` into the three.js camera, so everything here is layered
 * on the render camera only: the sim, `rig.rot` and `rig.pos` are never touched. Allocation-free per frame.
 *
 * Tuning: camera.json (validated in tuning.ts); `cameraTuning` is the live object the dev panel binds.
 */
import { Euler, Quaternion } from 'three/webgpu'
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { EV, EVENT_MASK, firstReadable } from '../../combat/events'
import { resetCameraRig } from '../../render/cameraRig'
import { Life, Ship, Vel } from '../../world/components'
import { createFovState, stepFov } from './fov'
import { addShakeDamage, createShakeState, stepShake, type ShakeOffset } from './shake'
import { cameraTuning, type CameraTuning } from './tuning'

/** Camera FOV changes smaller than this (degrees) skip the projection update. */
const FOV_EPSILON = 1e-4

export function createCameraFeature(tuning: CameraTuning = cameraTuning): GameFeature {
  const fov = createFovState()
  const shake = createShakeState()
  const jitter: ShakeOffset = { pitch: 0, yaw: 0, roll: 0 }
  const euler = new Euler(0, 0, 0, 'YXZ')
  const shakeRot = new Quaternion()
  let cursor = 0
  let lastPlayer = -1
  let baseFov = 0

  return {
    name: 'camera',
    order: FEATURE_ORDER.camera,

    init(ctx: GameContext): void {
      baseFov = ctx.data.world.camera.fovDeg
      cursor = ctx.sim.events.head
      lastPlayer = ctx.sim.playerEid
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      const p = ctx.sim.playerEid

      // A new player entity or a respawn: snap the chase arm and drop leftover shake.
      if (p !== lastPlayer) {
        lastPlayer = p
        resetCameraRig(ctx.rig)
        shake.amp = 0
      }

      // Damage the player took since the last frame.
      const ring = ctx.sim.events
      for (let i = firstReadable(ring, cursor); i < ring.head; i++) {
        const s = i & EVENT_MASK
        if (p < 0 || ring.eid[s] !== p) continue
        const type = ring.type[s]
        if (type === EV.hitShield) addShakeDamage(shake, tuning.shake, ring.amount[s], 0)
        else if (type === EV.hitHull) addShakeDamage(shake, tuning.shake, 0, ring.amount[s])
        else if (type === EV.respawn) {
          resetCameraRig(ctx.rig)
          shake.amp = 0
        }
      }
      cursor = ring.head

      const alive = p >= 0 && Life.alive[p] === 1
      const boosting = alive && Ship.boosting[p] === 1
      let speedFraction = 0
      if (alive) {
        const def = ctx.sim.defs[Ship.defIndex[p]]
        const vx = Vel.x[p]
        const vy = Vel.y[p]
        const vz = Vel.z[p]
        speedFraction = Math.sqrt(vx * vx + vy * vy + vz * vz) / (def.maxSpeed * def.boostSpeedMult)
      }
      const dt = f.paused ? 0 : f.dt

      // FOV: base (world.json) + eased boost kick + speed.
      const camera = ctx.camera
      const fovDeg = baseFov + stepFov(fov, tuning.fov, boosting, speedFraction, dt)
      if (Math.abs(camera.fov - fovDeg) > FOV_EPSILON) {
        camera.fov = fovDeg
        camera.updateProjectionMatrix()
      }

      // Shake: rotation jitter on the render camera only; steady while paused.
      const scale = ctx.rig.mode === 'cockpit' ? tuning.shake.cockpitScale : 1
      stepShake(shake, tuning.shake, dt, boosting, ctx.settings.screenShake, scale, jitter)
      if (f.paused || (jitter.pitch === 0 && jitter.yaw === 0 && jitter.roll === 0)) return
      euler.set(jitter.pitch, jitter.yaw, jitter.roll, 'YXZ')
      shakeRot.setFromEuler(euler)
      camera.quaternion.multiply(shakeRot)
    },

    dispose(): void {
      fov.kick = 0
      fov.speedDeg = 0
      shake.amp = 0
      shake.rumble = 0
    },
  }
}

const feature: GameFeature = createCameraFeature()

export default feature
